import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, Link, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { FILTER_FIELD_DEFINITIONS } from './AddFilterModal.types';
import { EditFilterPopover } from './EditFilterPopover';
import { ActiveFiltersBarProps, AdvancedFilter } from './FilterBar.props';
import { CONTROL_RADIUS, SURFACE_RADIUS, valuePillSx } from './filterBarStyles';

export const ActiveFiltersBar: React.FC<ActiveFiltersBarProps> = ({
  filters,
  onRemove,
  onClearAll,
  onEdit,
  fieldDefinitions,
  listOptions,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const fields = fieldDefinitions ?? FILTER_FIELD_DEFINITIONS;

  const editingFilter: AdvancedFilter | null =
    editIndex !== null && editIndex < filters.length ? filters[editIndex] : null;
  const editingField = useMemo(
    () => (editingFilter ? (fields.find((f) => f.key === editingFilter.dataKey) ?? null) : null),
    [editingFilter, fields]
  );

  const closeEditor = useCallback(() => {
    setAnchorEl(null);
    setEditIndex(null);
  }, []);

  const handleApplyEdit = useCallback(
    (filter: AdvancedFilter) => {
      if (onEdit && editIndex !== null) onEdit(editIndex, filter);
      closeEditor();
    },
    [onEdit, editIndex, closeEditor]
  );

  if (filters.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 1,
        bgcolor: theme.palette.v4.controlSurface,
        // Continues the strip above rather than starting a new band: same
        // border, corners only on the bottom edge.
        border: `1px solid ${theme.palette.v4.border}`,
        borderTopColor: theme.palette.v4.border,
        borderBottomLeftRadius: SURFACE_RADIUS,
        borderBottomRightRadius: SURFACE_RADIUS,
      }}
    >
      <Typography
        component="span"
        sx={{
          mr: 0.5,
          fontSize: '10.5px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: theme.palette.v4.textFaint,
        }}
      >
        Applied
      </Typography>

      {filters.map((filter, index) => {
        // Only offer edit when a handler is wired and the chip's field exists
        // in the catalog — otherwise the popover would have no form to show.
        const canEdit = Boolean(onEdit) && fields.some((f) => f.key === filter.dataKey);
        const handleClick = canEdit
          ? (e: React.MouseEvent<HTMLElement>) => {
              setAnchorEl(e.currentTarget);
              setEditIndex(index);
            }
          : undefined;
        return (
          <Chip
            key={`${filter.dataKey}-${index}`}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={valuePillSx(theme)}>
                  {filter.field}
                </Box>
                <Typography
                  component="span"
                  sx={{ fontSize: '12px', color: theme.palette.text.primary }}
                >
                  {filter.display}
                </Typography>
              </Box>
            }
            onClick={handleClick}
            onDelete={() => onRemove(index)}
            deleteIcon={<CloseIcon fontSize="small" aria-label={`Remove ${filter.field} filter`} />}
            variant="outlined"
            aria-label={canEdit ? `Edit ${filter.field} filter` : undefined}
            sx={{
              borderColor: theme.palette.v4.filterInputBorder,
              bgcolor: theme.palette.v4.controlSurface,
              borderRadius: CONTROL_RADIUS,
              height: 'auto',
              py: 0.375,
              cursor: canEdit ? 'pointer' : 'default',
              transition: 'border-color 120ms ease',
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-deleteIcon': {
                fontSize: '15px',
                ml: 0,
                mr: 0.25,
                color: theme.palette.v4.textFaint,
                '&:hover': { color: theme.palette.error.main },
              },
              ...(canEdit && {
                '&:hover': {
                  bgcolor: theme.palette.v4.controlSurface,
                  borderColor: theme.palette.v4.controlBorderHover,
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.v4.accent}`,
                  outlineOffset: 1,
                },
              }),
            }}
          />
        );
      })}

      {onClearAll && (
        <Link
          component="button"
          variant="body2"
          onClick={onClearAll}
          sx={{
            color: theme.palette.v4.accent,
            cursor: 'pointer',
            ml: 1,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Clear All
        </Link>
      )}

      {onEdit && (
        <EditFilterPopover
          anchorEl={anchorEl}
          field={editingField}
          filter={editingFilter}
          options={editingField ? listOptions?.[editingField.key] : undefined}
          onApply={handleApplyEdit}
          onClose={closeEditor}
        />
      )}
    </Box>
  );
};
